import { useEffect, useRef } from 'react';

import { IRestaurant } from '@rbi-ctg/store';
import { useAppStateChangeListener } from 'hooks/use-app-state-change-listener';
import useInterval from 'hooks/use-interval';
import { useSmartAlertsContext } from 'state/smart-alert';
import { ISmartAlertsContext } from 'state/smart-alert/types';
import { logger } from 'utils/logger';
import { addSecondsToISOString } from 'utils/time-utils';

import { useStoreContext } from '..';
import { clearStoreLocalTime, setStoreLocalTime } from '../utils';

const REFRESH_INTERVAL = 2_000; // 2 seconds
const SMART_ALERTS_UPDATE_INTERVAL = 60_000; // 1 minute

const handleStoreLocalTimeUpdate = (store: IRestaurant, baseTimes: BaseTimesRef) => {
  if (!store.localTime || !store.timezone) {
    logger.warn('no store local time or timezone set for the store');
    return null;
  }
  if (!baseTimes.storeLocalTime || !baseTimes.deviceTime) {
    setStoreLocalTime(store.localTime);
    return null;
  }

  const currentDeviceTime = Date.now();
  const elapsedSeconds = (currentDeviceTime - baseTimes.deviceTime) / 1000;

  const newLocalTime = addSecondsToISOString(
    baseTimes.storeLocalTime,
    elapsedSeconds,
    store.timezone
  );

  setStoreLocalTime(newLocalTime);

  return newLocalTime;
};

const handleSmartAlertsUpdate = (
  setCurrentStoreDate: ISmartAlertsContext['setCurrentStoreDate'],
  storeLocalTime: string | null,
  smartAlertsCycleCount: React.MutableRefObject<number>
) => {
  if (!storeLocalTime) {
    setCurrentStoreDate(null);
    return;
  }

  smartAlertsCycleCount.current += 1;

  if (
    smartAlertsCycleCount.current === 1 ||
    smartAlertsCycleCount.current >= SMART_ALERTS_UPDATE_INTERVAL / REFRESH_INTERVAL
  ) {
    setCurrentStoreDate(storeLocalTime);
    smartAlertsCycleCount.current = 1;
  }
};

type BaseTimesRef = {
  storeLocalTime: string | null;
  deviceTime: number | null;
};

export const StoreTimer = () => {
  const { store, noStoreSelected, fetchStore } = useStoreContext();
  const storeLocalTime = useRef<string | null>(null);
  const { addAppStateChangeListeners, removeAppStateChangeListeners } = useAppStateChangeListener();
  const { setCurrentStoreDate } = useSmartAlertsContext();
  const smartAlertsCycleCount = useRef<number>(0);
  const baseTime = useRef<BaseTimesRef>({
    storeLocalTime: null,
    deviceTime: null,
  });

  /**
   * This useEffect sets the base times for the store's local time (storeLocalTime)
   * and the device's time (deviceTime). By tracking these values, we can calculate
   * the correct store time even when the app goes to the background and the timer stops,
   * avoiding incorrect date displays when returning to the foreground.
   */
  useEffect(() => {
    smartAlertsCycleCount.current = 0;
    if (noStoreSelected) {
      clearStoreLocalTime();
      baseTime.current.storeLocalTime = null;
      baseTime.current.deviceTime = null;
    } else if (store?.localTime) {
      baseTime.current.storeLocalTime = store.localTime;
      baseTime.current.deviceTime = Date.now();
    } else {
      logger.warn('No store local time available at initialization');
    }
  }, [store.localTime, noStoreSelected]);

  /**
   * This useEffect listens for when the app returns from the background, and refetches the store
   * to get the latest store data, including the updated store time.
   * This updates the baseTime ref (when store changes), ensuring the store time stays accurate.
   */
  useEffect(() => {
    addAppStateChangeListeners(() => {
      if (!noStoreSelected && store.number) {
        fetchStore(store.number);
      } else {
        clearStoreLocalTime();
      }
    });

    return () => {
      removeAppStateChangeListeners();
    };
  }, [
    addAppStateChangeListeners,
    fetchStore,
    noStoreSelected,
    removeAppStateChangeListeners,
    store.number,
  ]);

  useInterval(() => {
    if (noStoreSelected) {
      clearStoreLocalTime();
    } else {
      storeLocalTime.current = handleStoreLocalTimeUpdate(store, baseTime.current);
      handleSmartAlertsUpdate(setCurrentStoreDate, storeLocalTime.current, smartAlertsCycleCount);
      // TODO: use more handlers here as needed like
      // handleStoreClose(store);
      // handleServiceModeClose(store);
      // handleDayPartClose(store);
    }
  }, REFRESH_INTERVAL);

  return null;
};
